import React, { useState, useEffect } from 'react'

interface MultiScreenshotPromptEditorProps {
  multiScreenshotPrompt: string
  onSave: (prompt: string) => void
  isLoading: boolean
}

const DEFAULT_MULTI_SCREENSHOT_PROMPT = `Analyze the following series of screenshots as a single, continuous document or interface. 

The images are provided in chronological order and may have overlapping content where the user scrolled or navigated between captures. Please:

1. Identify and intelligently merge any overlapping regions without duplicating information
2. Understand the spatial and chronological relationships between the images
3. Provide a comprehensive analysis based on the complete context across all images
4. If there are any gaps or unclear transitions between images, note them in your response

Screenshots to analyze:`

const MultiScreenshotPromptEditor: React.FC<MultiScreenshotPromptEditorProps> = ({
  multiScreenshotPrompt,
  onSave,
  isLoading,
}) => {
  const [prompt, setPrompt] = useState(multiScreenshotPrompt || DEFAULT_MULTI_SCREENSHOT_PROMPT)
  const [hasChanges, setHasChanges] = useState(false)
  const [showInstructions, setShowInstructions] = useState(false)

  useEffect(() => {
    setPrompt(multiScreenshotPrompt || DEFAULT_MULTI_SCREENSHOT_PROMPT)
    setHasChanges(false)
  }, [multiScreenshotPrompt])

  const handleChange = (value: string) => {
    setPrompt(value)
    setHasChanges(value !== (multiScreenshotPrompt || DEFAULT_MULTI_SCREENSHOT_PROMPT))
  }

  const handleSave = () => {
    onSave(prompt)
    setHasChanges(false)
  }

  const handleReset = () => {
    setPrompt(multiScreenshotPrompt || DEFAULT_MULTI_SCREENSHOT_PROMPT)
    setHasChanges(false)
  }

  const handleUseDefault = () => {
    setPrompt(DEFAULT_MULTI_SCREENSHOT_PROMPT)
    setHasChanges(DEFAULT_MULTI_SCREENSHOT_PROMPT !== (multiScreenshotPrompt || DEFAULT_MULTI_SCREENSHOT_PROMPT))
  }

  return (
    <div className="space-y-4">
      {/* Instructions Toggle */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900">Multi-Screenshot Session Prompt</h4>
        <button
          onClick={() => setShowInstructions(!showInstructions)}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          {showInstructions ? 'Hide Instructions' : 'Show Instructions'}
        </button>
      </div>

      {/* Instructions Panel */}
      {showInstructions && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h5 className="text-sm font-medium text-blue-900 mb-2">Usage Instructions</h5>
          <div className="text-sm text-blue-800 space-y-2">
            <p>
              This prompt is used when processing multi-screenshot sessions (CTRL+SHIFT+5 → CTRL+SHIFT+6 → CTRL+SHIFT+7).
            </p>
            <p>
              <strong>Best Practices:</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Include instructions for handling overlapping content from scrolling</li>
              <li>Guide the AI to understand chronological relationships between images</li>
              <li>Ask for comprehensive analysis across all screenshots</li>
              <li>Request identification of gaps or unclear transitions</li>
            </ul>
            <p>
              <strong>Example Use Cases:</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Long documents that require scrolling to capture completely</li>
              <li>Multi-step workflows or processes</li>
              <li>Complex interfaces with multiple screens or tabs</li>
              <li>Sequential error messages or system states</li>
            </ul>
          </div>
        </div>
      )}

      {/* Prompt Editor */}
      <div className="space-y-3">
        <textarea
          value={prompt}
          onChange={(e) => handleChange(e.target.value)}
          placeholder="Enter your multi-screenshot session prompt..."
          className="w-full h-40 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-vertical"
          disabled={isLoading}
        />
        
        {/* Character Count */}
        <div className="flex justify-between items-center text-sm text-gray-500">
          <span>{prompt.length} characters</span>
          {prompt.length > 2000 && (
            <span className="text-amber-600">Consider shortening for better performance</span>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={handleSave}
            disabled={!hasChanges || isLoading}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            onClick={handleReset}
            disabled={!hasChanges || isLoading}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Reset
          </button>
          <button
            onClick={handleUseDefault}
            disabled={isLoading}
            className="bg-blue-100 text-blue-700 px-4 py-2 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Use Default Template
          </button>
        </div>
      </div>

      {/* Preview Section */}
      <div className="border-t pt-4">
        <h5 className="text-sm font-medium text-gray-900 mb-2">Preview</h5>
        <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{prompt}</p>
        </div>
      </div>
    </div>
  )
}

export default MultiScreenshotPromptEditor